/**
 * File Upload component with drag and drop support
 */

import React, { useRef, useState, useCallback } from 'react';
import Button from './button.js';

/**
 * FileUpload component
 * @param {Object} props - Component props
 * @param {Function} props.onFileSelect - Callback when file is selected
 * @param {Array} props.acceptedTypes - Accepted file types (e.g., ['.csv', '.xlsx'])
 * @param {number} props.maxSize - Maximum file size in MB
 * @param {string} props.label - Upload area label
 * @param {string} props.selectFileLabel - Label for select file button
 * @param {boolean} props.multiple - Allow multiple files
 * @param {string} props.className - Additional CSS classes
 */
const FileUpload = ({
  onFileSelect,
  acceptedTypes = ['.csv', '.xlsx', '.xls'],
  maxSize = 10, // MB
  label,
  acceptedLabelTemplate = 'Accepted: {types} (max {size}MB)',
  selectFileLabel = 'Select File',
  multiple = false,
  className = ''
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  const validateFile = useCallback((file) => {
    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return {
        valid: false,
        error: `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`
      };
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxSize}MB limit. Current size: ${fileSizeMB.toFixed(2)}MB`
      };
    }

    return { valid: true };
  }, [acceptedTypes, maxSize]);

  const handleFile = useCallback((file) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      if (onFileSelect) {
        onFileSelect(null, validation.error);
      }
      return;
    }

    setError(null);
    setSelectedFile(file);
    if (onFileSelect) {
      onFileSelect(file, null);
    }
  }, [validateFile, onFileSelect]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileSelect) {
      onFileSelect(null, null);
    }
  }, [onFileSelect]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const acceptedTypesString = acceptedTypes.join(', ');
  const acceptedLabel = acceptedLabelTemplate
    ?.replace('{types}', acceptedTypesString)
    ?.replace('{size}', maxSize);

  return (
    <div className={className}>
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${isDragging 
            ? 'border-customOrange bg-orange-50 dark:bg-orange-900/10' 
            : 'border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#161b22] hover:border-gray-400 dark:hover:border-[#484f58]'
          }
          ${error ? 'border-red-300 dark:border-red-800' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypesString}
          multiple={multiple}
          onChange={handleFileInputChange}
          className="hidden"
        />

        {!selectedFile ? (
          <>
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-[#8b949e] mb-4"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-sm text-gray-600 dark:text-[#b1bac4] mb-2">
              {label || 'Drag and drop a file here, or click to select'}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#8b949e] mb-4">
              {acceptedLabel || `Accepted: ${acceptedTypesString} (max ${maxSize}MB)`}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClick}
            >
              {selectFileLabel}
            </Button>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 min-w-0">
              <svg
                className="h-8 w-8 text-green-500 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9] truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-[#8b949e]">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveFile}
              className="ml-4"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
        )}

        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
